import * as React from 'react'
import { FileUp, Loader2, ScanSearch, Upload, X } from 'lucide-react'
import {
  useImportTagBundleMutation,
  type TagBundleGraphResult,
  type TagBundleImportMode,
  type TagBundleImportResult,
  type TagBundleUrlRewrite
} from '@axilon-apps/domains-ontology-ui-state'

import { Button } from '../../../components/ui/button'
import { Card, CardContent } from '../../../components/ui/card'
import { cn } from '../../../lib/utils'
import { ConfirmDialog } from '../../ConfigurePage/components/DeleteConfirmDialog'

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (error && typeof error === 'object') {
    const obj = error as Record<string, unknown>
    if (obj['data'] && typeof obj['data'] === 'object') {
      const data = obj['data'] as Record<string, unknown>
      if (typeof data['message'] === 'string') return data['message']
      if (typeof data['error'] === 'string') return data['error']
    }
    if (typeof obj['message'] === 'string') return obj['message']
    if (typeof obj['error'] === 'string') return obj['error']
  }
  return 'Unknown error.'
}

/** Default local ports per scheme, so the common "point it at my box" case is
 * one keystroke rather than a full retype. */
const LOCAL_PORTS: Array<{ test: RegExp; local: string }> = [
  { test: /^opc\.tcp:/i, local: 'opc.tcp://localhost:4840' },
  { test: /^https?:\/\/[^/]*:5000/i, local: 'http://localhost:5000' },
  { test: /^https?:/i, local: 'http://localhost:8088' }
]

/**
 * suggestLocalUrl maps a bundle's source URL onto a plausible local address,
 * preserving any path suffix. Exported for tests: the mapping is the operator's
 * starting point, so a wrong guess is worse than no guess.
 */
export function suggestLocalUrl(sourceUrl: string): string {
  for (const { test, local } of LOCAL_PORTS) {
    if (test.test(sourceUrl)) return local
  }
  return sourceUrl
}

/**
 * Renders the graph rewrite as one line, naming the branch because the graph
 * write targets whichever branch is active rather than a fixed one. Returns
 * empty when the graph was skipped: that reason already travels as a warning,
 * and repeating it here would say the same thing twice.
 */
export function graphSummary(graph: TagBundleGraphResult | undefined, applied: boolean): string {
  if (!graph || graph.skipped_reason) return ''
  const perLabel = Object.entries(graph.applied ?? {}).filter(([, count]) => count > 0)
  const total = perLabel.reduce((sum, [, count]) => sum + count, 0)
  const verb = applied ? 'Rewrote' : 'Would rewrite'
  if (total === 0) return `${verb} no graph URLs on branch ${graph.branch}`
  const breakdown = perLabel.map(([label, count]) => `${label} ${count}`).join(', ')
  return `${verb} ${total} graph URL${total === 1 ? '' : 's'} on branch ${graph.branch} (${breakdown})`
}

/**
 * Imports a portable tag bundle.
 *
 * The flow is analyze-then-apply. The dry run reports which organization the
 * rows would land in and which source URLs the bundle references, because the
 * import never asks for a target organization — it uses the caller's identity —
 * and remapping the URLs is the whole reason the file is portable.
 */
export function TagBundleImportCard({
  embedded = false,
  allowReplaceMode = true,
  onImported
}: {
  embedded?: boolean
  allowReplaceMode?: boolean
  onImported?: (result: TagBundleImportResult) => void
} = {}) {
  const [importTagBundle, importState] = useImportTagBundleMutation()
  const [file, setFile] = React.useState<File | null>(null)
  const [analysis, setAnalysis] = React.useState<TagBundleImportResult | null>(null)
  const [applied, setApplied] = React.useState<TagBundleImportResult | null>(null)
  const [rewrites, setRewrites] = React.useState<Record<string, string>>({})
  const [mode, setMode] = React.useState<TagBundleImportMode>('merge')
  const [confirmOpen, setConfirmOpen] = React.useState(false)
  const [error, setError] = React.useState('')
  const [fileInputKey, setFileInputKey] = React.useState(0)
  const fileInputRef = React.useRef<HTMLInputElement>(null)
  // Analyze and Import drive the same mutation, so which button owns the
  // spinner has to be tracked explicitly. It cannot be derived from the confirm
  // dialog being open: the dialog's action button closes it on click, so an
  // import would show its spinner on Analyze for the whole run.
  const [pending, setPending] = React.useState<'analyze' | 'import' | null>(null)

  const busy = importState.isLoading

  React.useEffect(() => {
    if (!allowReplaceMode) setMode('merge')
  }, [allowReplaceMode])

  const clearFile = React.useCallback(() => {
    setFile(null)
    setAnalysis(null)
    setApplied(null)
    setRewrites({})
    setFileInputKey((key) => key + 1)
    setError('')
  }, [])

  const rewriteRules = React.useCallback((): TagBundleUrlRewrite[] => {
    const sources = analysis?.manifest.source_urls ?? []
    return sources
      .map((from) => ({ from, to: (rewrites[from] ?? '').trim() }))
      .filter((rule) => rule.to !== '' && rule.to !== rule.from)
  }, [analysis, rewrites])

  const runAnalyze = React.useCallback(async () => {
    if (!file) return
    setError('')
    setApplied(null)
    setPending('analyze')
    try {
      // Rewrites are sent on analyze too — empty on the first pass, then
      // whatever the operator filled in. Both the collision check and the graph
      // node counts only have something to say once a map exists, so a re-analyze
      // is what turns Analyze into a real preview of the import.
      const result = await importTagBundle({
        file,
        dryRun: true,
        mode,
        urlRewrites: rewriteRules()
      }).unwrap()
      setAnalysis(result)
      setRewrites((current) => {
        const next = { ...current }
        for (const url of result.manifest.source_urls) {
          next[url] ??= suggestLocalUrl(url)
        }
        return next
      })
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setPending(null)
    }
  }, [file, importTagBundle, mode, rewriteRules])

  const runImport = React.useCallback(async () => {
    if (!file) return
    setError('')
    setPending('import')
    try {
      const result = await importTagBundle({
        file,
        mode,
        urlRewrites: rewriteRules()
      }).unwrap()
      setApplied(result)
      setAnalysis(null)
      onImported?.(result)
    } catch (err) {
      setError(getErrorMessage(err))
    } finally {
      setPending(null)
      setConfirmOpen(false)
    }
  }, [file, importTagBundle, mode, onImported, rewriteRules])

  // The graph rewrite follows the rewrite map, so the confirm step says which
  // branch's graph will change alongside the tag rows.
  const pendingRewrites = rewriteRules().length
  const graphBranch = analysis?.graph?.branch
  const graphConfirmNote =
    pendingRewrites > 0
      ? `The same URL rewrites are applied to the ${graphBranch ? `“${graphBranch}” ` : ''}branch’s graph so gateway-scoped agent tools resolve them too.`
      : ''

  const body = (
    <div className="flex flex-col gap-2">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="text-sm font-medium text-ink-2">Import tag registry</div>
          <p className="m-0 text-11 text-ink-3">
            <span className="font-mono">.ndjson.gz</span> → this organization, with gateway URLs
            remapped on the way in
          </p>
        </div>
        <div className="flex shrink-0 gap-1.5">
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!file || busy}
            onClick={() => void runAnalyze()}
          >
            {pending === 'analyze' ? <Loader2 className="animate-spin" /> : <ScanSearch />}
            Analyze
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!file || !analysis || busy}
            onClick={() => {
              setError('')
              setConfirmOpen(true)
            }}
          >
            {pending === 'import' ? <Loader2 className="animate-spin" /> : <Upload />}
            Import
          </Button>
        </div>
      </div>

      <input
        key={fileInputKey}
        ref={fileInputRef}
        aria-label="Tag bundle file"
        type="file"
        accept=".gz,application/gzip"
        className="sr-only"
        disabled={busy}
        onChange={(event) => {
          setFile(event.target.files?.[0] ?? null)
          setAnalysis(null)
          setApplied(null)
          setError('')
        }}
      />

      {file ? (
        <div className="flex h-8 min-w-0 items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2">
          <FileUp className="size-3.5 shrink-0 text-ink-3" aria-hidden="true" />
          <span className="min-w-0 flex-1 truncate font-mono text-11 text-ink-2" title={file.name}>
            {file.name}
          </span>
          <button
            type="button"
            aria-label="Clear bundle file"
            className="shrink-0 rounded p-0.5 text-ink-3 hover:bg-muted hover:text-ink-2 disabled:opacity-50"
            disabled={busy}
            onClick={clearFile}
          >
            <X className="size-3.5" />
          </button>
        </div>
      ) : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="h-8 self-start"
          disabled={busy}
          onClick={() => fileInputRef.current?.click()}
        >
          <FileUp />
          Choose .ndjson.gz
        </Button>
      )}

      <div className="flex flex-wrap items-center gap-1">
        {(allowReplaceMode ? (['merge', 'replace'] as const) : (['merge'] as const)).map((id) => (
          <button
            key={id}
            type="button"
            aria-pressed={mode === id}
            onClick={() => setMode(id)}
            disabled={busy}
            className={cn(
              'rounded-md border px-2 py-1 text-11 font-medium capitalize transition-colors',
              mode === id
                ? 'border-border bg-card text-ink shadow-sm'
                : 'border-transparent text-ink-3 hover:bg-card/60 hover:text-ink-2',
              mode === id && id === 'replace' ? 'text-st-down' : null
            )}
          >
            {id}
          </button>
        ))}
        <span className="text-11 text-ink-3">
          {mode === 'merge'
            ? 'Upserts; leaves unrelated local tags alone'
            : 'Clears this org’s tags first, so the bundle is authoritative'}
        </span>
      </div>

      {analysis ? (
        <div className="flex flex-col gap-1.5 rounded-md border border-border bg-muted/30 px-2 py-1.5">
          <div className="text-11 text-ink-2">
            {analysis.applied.tags} tags from org{' '}
            <span className="font-mono">
              {analysis.manifest.source_organization_id || 'unknown'}
            </span>{' '}
            → org <span className="font-mono">{analysis.target_organization_id || 'current'}</span>
            {analysis.applied.readings > 0 ? ` · ${analysis.applied.readings} readings` : null}
          </div>
          {analysis.manifest.source_urls.length > 0 ? (
            <div className="flex flex-col gap-1">
              <div className="text-11 font-medium text-ink-3">Rewrite URLs</div>
              {analysis.manifest.source_urls.map((url) => (
                <label key={url} className="flex min-w-0 items-center gap-1.5">
                  <span
                    className="min-w-0 flex-1 truncate font-mono text-11 text-ink-3"
                    title={url}
                  >
                    {url}
                  </span>
                  <span className="shrink-0 text-11 text-ink-3" aria-hidden>
                    →
                  </span>
                  <input
                    type="text"
                    aria-label={`Target URL for ${url}`}
                    value={rewrites[url] ?? ''}
                    disabled={busy}
                    onChange={(event) =>
                      setRewrites((current) => ({
                        ...current,
                        [url]: event.target.value
                      }))
                    }
                    className="h-7 min-w-0 flex-1 rounded-md border border-border bg-card px-2 font-mono text-11 text-ink-2"
                  />
                </label>
              ))}
              <p className="m-0 text-11 text-ink-3">
                Re-analyze after filling these in to preview URL collisions and the graph rewrite.
              </p>
            </div>
          ) : null}
          {graphSummary(analysis.graph, false) ? (
            <div className="text-11 text-ink-3">{graphSummary(analysis.graph, false)}</div>
          ) : null}
          {(analysis.warnings ?? []).map((warning) => (
            <div key={warning} className="text-11 text-st-warn">
              {warning}
            </div>
          ))}
        </div>
      ) : null}

      {applied ? (
        <div className="flex flex-col gap-1 rounded-md border border-border bg-muted/30 px-2 py-1.5">
          <div className="text-11 text-ink-2">
            Imported {applied.applied.tags} tags into org{' '}
            <span className="font-mono">{applied.target_organization_id || 'current'}</span>
            {applied.applied.readings > 0 ? ` · ${applied.applied.readings} readings` : null}
            {applied.applied.tags_skipped > 0 ? ` · ${applied.applied.tags_skipped} skipped` : null}
            {applied.applied.tags_failed > 0 ? ` · ${applied.applied.tags_failed} failed` : null}
          </div>
          {(applied.rewrites ?? []).map((rule) => (
            <div key={rule.from} className="truncate font-mono text-11 text-ink-3">
              {rule.from} → {rule.to} ({rule.occurrences})
            </div>
          ))}
          {graphSummary(applied.graph, true) ? (
            <div className="text-11 text-ink-3">{graphSummary(applied.graph, true)}</div>
          ) : null}
          {(applied.warnings ?? []).map((warning) => (
            <div key={warning} className="text-11 text-st-warn">
              {warning}
            </div>
          ))}
        </div>
      ) : null}

      {error ? (
        <div role="alert" className="rounded-md bg-st-down-bg px-2 py-1 text-11 text-st-down">
          {error}
        </div>
      ) : null}
    </div>
  )

  const dialog = (
    <ConfirmDialog
      open={confirmOpen}
      onOpenChange={setConfirmOpen}
      title={mode === 'replace' ? 'Replace this org’s tag registry?' : 'Import tag registry?'}
      description={[
        mode === 'replace'
          ? 'Deletes this organization’s tags, endpoints, and database connections, then imports the bundle in their place. Readings are cleared only if the bundle carries readings. This cannot be undone.'
          : 'Upserts the bundle’s tags into this organization. Re-running the same import is safe; tags already present without a graph node URN are reported as skipped rather than overwritten.',
        // Named explicitly because the graph write lands on whichever branch is
        // active, which is a wider blast radius than this org's tag tables.
        graphConfirmNote
      ]
        .filter(Boolean)
        .join(' ')}
      confirmLabel={mode === 'replace' ? 'Replace registry' : 'Import'}
      busy={busy}
      onConfirm={() => void runImport()}
    />
  )

  if (embedded) {
    return (
      <>
        <div className="rounded-xl border bg-card px-4 py-3 shadow-sm">{body}</div>
        {dialog}
      </>
    )
  }

  return (
    <Card className="gap-0 py-0 shadow-sm">
      <CardContent className="px-4 py-2.5">{body}</CardContent>
      {dialog}
    </Card>
  )
}
